import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Heart, Target, Users, TrendingUp, CheckCircle, Lightbulb, Award, Clock, Star, Lock, Eye, FileText } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const OurCommitments = () => {
  const coreCommitments = [
    {
      icon: Target,
      title: "Results-Driven Excellence",
      description: "We're committed to delivering measurable ROI and tangible business growth. Every strategy is designed with your success metrics in mind.",
      promise: "Guaranteed measurable results within 90 days or we'll work for free until you see them."
    },
    {
      icon: Shield,
      title: "Complete Transparency",
      description: "No hidden fees, no locked contracts, no confusing jargon. You'll know exactly what we're doing and why at every step.",
      promise: "Full access to all reporting, strategies, and campaign data with weekly transparent updates."
    },
    {
      icon: Heart,
      title: "Partnership First",
      description: "We don't just work for you - we work with you as an extension of your team, deeply understanding your business goals.",
      promise: "Direct access to our senior team members, not junior staff or outsourced work."
    },
    {
      icon: Lightbulb,
      title: "Innovation & Best Practices",
      description: "We stay ahead of digital trends and implement cutting-edge strategies while maintaining proven fundamentals.",
      promise: "Continuous optimization and adaptation to the latest industry changes and algorithm updates."
    }
  ];

  const serviceCommitments = [
    {
      icon: Clock,
      title: "Response Time",
      commitment: "2-hour response guarantee on all communications"
    },
    {
      icon: Award,
      title: "Quality Assurance",
      commitment: "Every deliverable reviewed by senior team before delivery"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      commitment: "Assigned account manager who knows your business inside out"
    },
    {
      icon: TrendingUp,
      title: "Continuous Growth",
      commitment: "Monthly strategy reviews and optimization recommendations"
    },
    {
      icon: Star,
      title: "Client Success",
      commitment: "We don't succeed unless you succeed - aligned incentives always"
    },
    {
      icon: CheckCircle,
      title: "Delivery Promise",
      commitment: "On-time delivery of all projects and campaigns, guaranteed"
    }
  ];

  const values = [
    "Honesty in all communications and reporting",
    "Ethical marketing practices that build long-term trust",
    "Data-driven decisions backed by real performance metrics",
    "Continuous learning and adaptation to market changes",
    "Client success as our primary business metric",
    "Sustainable growth strategies, not short-term tricks"
  ];

  return (
    <>
      <EnhancedSEO
        title="Commitment - Vincent Growth Partners"
        description="Discover our unwavering commitments to client success, transparency, and results-driven digital marketing. See why businesses trust VGP with their growth."
        keywords="client commitments, digital marketing promises, business growth guarantees, marketing agency values, transparent reporting"
        url="https://vincentgrowthpartners.com/our-commitments"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary via-primary to-primary-light">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="text-secondary">Commitment</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
                These aren't just promises - they're the foundation of how we do business. 
                Your success is our success, and we're committed to proving it every day.
              </p>
            </div>
          </div>
        </section>

        {/* Core Commitments */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Our Core <span className="text-secondary">Promises</span>
              </h2>
              
              <div className="grid lg:grid-cols-2 gap-8">
                {coreCommitments.map((commitment, index) => (
                  <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                          <commitment.icon className="w-6 h-6 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-foreground mb-3">{commitment.title}</h3>
                          <p className="text-muted-foreground mb-4 leading-relaxed">{commitment.description}</p>
                          <div className="bg-secondary/10 border-l-4 border-secondary p-4 rounded-r">
                            <p className="font-medium text-foreground italic">{commitment.promise}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Service Commitments */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Service <span className="text-secondary">Standards</span>
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {serviceCommitments.map((item, index) => (
                  <Card key={index} className="border-0 shadow-elegant hover:shadow-2xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-6 h-6 text-secondary" />
                      </div>
                      <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.commitment}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Our <span className="text-secondary">Values</span> in Action
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-6 h-6 text-secondary mr-4 flex-shrink-0" />
                    <span className="text-lg text-muted-foreground">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Privacy & Data Protection Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Privacy & Data <span className="text-secondary">Protection</span>
              </h2>
              
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-4 text-secondary mb-4">
                  <Award className="w-8 h-8" />
                  <span className="text-xl font-semibold">3+ Years Cybersecurity Experience</span>
                </div>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Your business data and strategic insights are protected with enterprise-grade security 
                  and strict compliance standards.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <Card className="border-2 border-secondary/20 shadow-elegant hover:shadow-luxury transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">Enterprise Security</h3>
                    <p className="text-muted-foreground">
                      AES-256 encryption, secure infrastructure, and 24/7 monitoring protect your sensitive business information.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-secondary/20 shadow-elegant hover:shadow-luxury transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">Secure Data Retention</h3>
                    <p className="text-muted-foreground">
                      Your ICP data, brainstorm sessions, and strategic insights are securely stored and locked down for 3 years.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-secondary/20 shadow-elegant hover:shadow-luxury transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Eye className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">Privacy by Design</h3>
                    <p className="text-muted-foreground">
                      GDPR compliant data handling with controlled access and transparent privacy practices.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center">
                <Button variant="outline" size="lg" asChild>
                  <a href="/privacy-policy">
                    <FileText className="w-5 h-5 mr-2" />
                    View Full Privacy Policy
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Accountability Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Holding Ourselves <span className="text-secondary">Accountable</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                We believe in being held accountable for our commitments. That's why we track, 
                measure, and report on our performance against these promises.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="border-0 shadow-elegant">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2">100%</div>
                    <p className="text-muted-foreground">Commitment Fulfillment Rate</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-elegant">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2">2hrs max</div>
                    <p className="text-muted-foreground">Average Response Time</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-elegant">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2">100%</div>
                    <p className="text-muted-foreground">Client Satisfaction Score</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Talent & Community Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center text-foreground">
                🛠️ Our Commitment to <span className="text-secondary">Talent & Community</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-12 text-center leading-relaxed">
                At Vincent Growth Partners, we are committed to building a high-performance environment where young professionals thrive through:
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Radical Ownership</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Every team member is empowered to take full responsibility for outcomes. We reward results, not attendance.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Remote Excellence</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We've engineered systems that support 24/7 execution across time zones. Our team operates with asynchronous workflows, cloud-based performance tracking, and daily stand-ups to stay accountable.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Transparent Delivery</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Our clients and team benefit from live dashboards that track campaign ROI and project milestones in real-time. Internally, we use Notion and ClickUp to keep all resources accessible.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Continuous Learning</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Every team member receives a curated internal resource library, access to paid strategy breakdowns, and bi-weekly mentorship sessions focused on real client scenarios.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Inclusive, Fast-Moving Culture</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We embrace a culture where speed and clarity win — and where anyone can lead, regardless of age or location. You don't need permission to act. You just need proof that it works.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-primary-light">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Experience These Commitments?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Schedule a free strategy call and see how our commitments translate 
                into real results for your business.
              </p>
              <Button variant="premium" size="xl" asChild>
                <a href="https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today" target="_blank" rel="noopener noreferrer">
                  Book Your Strategy Call
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OurCommitments;